import React, { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import Icon from "../components/icons.jsx";
import { Eyebrow, PhotoFrame } from "../components/ui.jsx";
import { useLocaleNav } from "../lib/useLocaleNav.js";
import { goToBooking } from "../lib/handoff.js";

export default function HomeBold() {
  const go = useLocaleNav();
  return (
    <main>
      <BoldHero go={go} />
      <BoldTicker />
      <BoldNumbers />
      <BoldFeatures />
      <BoldFounder />
      <BoldPartnerMarquee />
      <BoldCTA />
    </main>
  );
}

function BoldHero({ go }) {
  const { t } = useTranslation();
  return (
    <section style={{ position: "relative", overflow: "hidden", minHeight: "100vh", background: "var(--ink)", color: "var(--bg)", isolation: "isolate" }}>
      <div aria-hidden="true" style={{ position: "absolute", inset: 0, zIndex: 1, overflow: "hidden", pointerEvents: "none" }}>
        <video
          autoPlay
          muted
          loop
          playsInline
          preload="auto"
          poster="/assets/TableTalk_hero_poster.jpg"
          src="/assets/TableTalk_hero.mp4"
          style={{ position: "absolute", inset: 0, width: "100%", height: "100%", objectFit: "cover", border: 0, pointerEvents: "none" }}
        />
      </div>
      <div className="container" style={{ position: "relative", zIndex: 4, minHeight: "100vh", display: "flex", flexDirection: "column", justifyContent: "flex-end", padding: "120px 24px 80px" }}>
        <div style={{ fontFamily: "Inter, sans-serif", fontSize: 13, letterSpacing: ".22em", textTransform: "uppercase", fontWeight: 600, color: "rgba(255,255,255,.78)" }}>
          {t("common.tagline")}
        </div>
        <h1 style={{ margin: "24px 0 0", maxWidth: "18ch", fontFamily: "Satoshi, Inter, sans-serif", fontWeight: 700, fontSize: "clamp(48px, 8.4vw, 156px)", lineHeight: 0.94, letterSpacing: "-0.045em", color: "#fff", textShadow: "0 2px 32px rgba(0,0,0,.4)" }}>
          {t("home.hero.headline")}{" "}
          <span style={{ display: "block", fontFamily: "Fraunces, serif", fontStyle: "italic", fontWeight: 400, color: "var(--persimmon)" }}>{t("home.hero.headlineItalic")}</span>
        </h1>
        <div style={{ marginTop: 48, display: "flex", gap: 12, flexWrap: "wrap" }}>
          <a href="#" className="btn btn-lg" onClick={(e) => { e.preventDefault(); go("pricing"); }} style={{ background: "var(--persimmon)", color: "var(--ink)" }}>
            {t("common.startForFree")} <Icon name="arrow-right" size={16} />
          </a>
          {/* Book a demo button hidden for now */}
          {false && (
            <a href="#" className="btn btn-lg" onClick={(e) => { e.preventDefault(); goToBooking(); }} style={{ background: "rgba(255,255,255,.08)", color: "#fff", border: "1px solid rgba(255,255,255,.32)", backdropFilter: "blur(8px)" }}>
              {t("common.bookDemo")}
            </a>
          )}
        </div>
      </div>
    </section>
  );
}

function BoldTicker() {
  const { t } = useTranslation();
  const items = t("home.ticker.items", { returnObjects: true });
  const row = [...items, ...items];
  return (
    <section style={{ borderBottom: "1px solid var(--line)", borderTop: "1px solid var(--line)", background: "var(--bg)", padding: "22px 0", overflow: "hidden" }}>
      <div style={{ display: "flex", gap: 0, whiteSpace: "nowrap", animation: "tt-marquee 38s linear infinite" }}>
        {row.map((tx, i) => (
          <span key={i} style={{ paddingInline: "28px", fontFamily: "Satoshi, Inter, sans-serif", fontSize: 14, fontWeight: 500, color: "var(--ink-2)", display: "inline-flex", alignItems: "center", gap: 14 }}>
            <span style={{ width: 6, height: 6, borderRadius: "50%", background: "var(--persimmon)" }} />
            {tx}
          </span>
        ))}
      </div>
      <style>{`
        @keyframes tt-marquee { from { transform: translateX(0); } to { transform: translateX(-50%); } }
        @media (prefers-reduced-motion: reduce) { @keyframes tt-marquee { from{transform:translateX(0)} to{transform:translateX(0)} } }
      `}</style>
    </section>
  );
}

const RO_HOUR_FORMATTER = new Intl.DateTimeFormat("en-US", {
  hour: "numeric", hour12: false, timeZone: "Europe/Bucharest"
});

function FeedbackCounter() {
  const BASE = 12500;
  // Counter = BASE at this instant; counts up indefinitely from here.
  // To "reset" the counter back to BASE, move START forward to the new now.
  const START = Date.parse("2026-05-15T08:00:00Z");
  const ACTIVE_START_HOUR = 10; // 10:00 Bucharest
  const ACTIVE_END_HOUR = 23;   // 23:00 Bucharest (exclusive)
  const TARGET_PER_DAY = 200;
  const ACTIVE_SEC_PER_DAY = (ACTIVE_END_HOUR - ACTIVE_START_HOUR) * 3600; // 46800
  const RATE = TARGET_PER_DAY / ACTIVE_SEC_PER_DAY; // entries per active second
  const INCREMENTS = [1, 2, 1, 3, 1, 5, 2, 1, 7, 1, 4, 1, 9, 2, 1];
  const MIN_INTERVAL_MS = 4 * 60 * 1000;
  const MAX_INTERVAL_MS = 15 * 60 * 1000;

  function isActiveAt(date) {
    const h = parseInt(RO_HOUR_FORMATTER.format(date), 10);
    return h >= ACTIVE_START_HOUR && h < ACTIVE_END_HOUR;
  }

  // DST-aware: walks minute-by-minute from `fromMs` to `toMs`, counting only minutes
  // whose local Bucharest hour falls inside the active window. Runs once on mount.
  function activeSecondsBetween(fromMs, toMs) {
    if (toMs <= fromMs) return 0;
    let total = 0;
    const STEP = 60 * 1000;
    for (let t = fromMs; t < toMs; t += STEP) {
      if (isActiveAt(new Date(t))) total += Math.min(STEP, toMs - t) / 1000;
    }
    return total;
  }

  const [count, setCount] = useState(() => {
    const activeSec = activeSecondsBetween(START, Date.now());
    return BASE + Math.floor(activeSec * RATE);
  });

  useEffect(() => {
    let timer;
    function scheduleNext() {
      const delay = MIN_INTERVAL_MS + Math.floor(Math.random() * (MAX_INTERVAL_MS - MIN_INTERVAL_MS));
      timer = setTimeout(() => {
        // Only tick during active hours. Outside the window, reschedule silently.
        if (isActiveAt(new Date())) {
          const inc = INCREMENTS[Math.floor(Math.random() * INCREMENTS.length)];
          setCount(c => c + inc);
        }
        scheduleNext();
      }, delay);
    }
    scheduleNext();
    return () => clearTimeout(timer);
  }, []);

  return <>{count.toLocaleString("en-US")}</>;
}

function BoldNumbers() {
  const { t } = useTranslation();
  const go = useLocaleNav();
  const stats = [
    { v: <FeedbackCounter />, l: t("home.numbers.feedbackLabel"),         sub: t("home.numbers.feedbackSub") },
    { v: "4.9×",               l: t("home.numbers.publicReviewsLabel"),    sub: t("home.numbers.publicReviewsSub") },
    { v: "6.9×",               l: t("home.numbers.negativeReviewsLabel"),  sub: t("home.numbers.negativeReviewsSub") }
  ];
  return (
    <section style={{ background: "var(--persimmon)", color: "var(--ink)", padding: "140px 0", borderBottom: "1px solid var(--line)" }}>
      <div className="container">
        <div style={{ fontFamily: "Inter, sans-serif", fontSize: 12.5, letterSpacing: ".18em", textTransform: "uppercase", fontWeight: 600, color: "rgba(0,0,0,.65)" }}>{t("home.numbers.eyebrow")}</div>
        <h2 style={{ margin: "24px 0 0", fontFamily: "Satoshi, Inter, sans-serif", fontWeight: 700, fontSize: "clamp(48px, 6vw, 96px)", lineHeight: 0.95, letterSpacing: "-0.04em", maxWidth: 1100, color: "var(--ink)" }}>
          {t("home.numbers.headline")}{" "}
          <span style={{ fontFamily: "Fraunces, serif", fontStyle: "italic", fontWeight: 400 }}>{t("home.numbers.headlineItalic")}</span>
        </h2>
        <div style={{ marginTop: 96, display: "grid", gridTemplateColumns: "repeat(3, minmax(0, 1fr))", gap: 0, borderTop: "1px solid rgba(0,0,0,.18)" }} className="big-num-grid">
          {stats.map((s, i) => (
            <div key={i} style={{ padding: "48px 24px 0", borderRight: i < 2 ? "1px solid rgba(0,0,0,.18)" : 0 }} className="big-num-cell">
              <div style={{ fontFamily: "Satoshi, Inter, sans-serif", fontWeight: 700, fontSize: "clamp(56px, 8vw, 116px)", lineHeight: 0.9, letterSpacing: "-0.04em", color: "var(--ink)", whiteSpace: "nowrap" }}>{s.v}</div>
              <div style={{ marginTop: 24, fontFamily: "Satoshi, sans-serif", fontWeight: 600, fontSize: 18 }}>{s.l}</div>
              {s.sub && (
                <div style={{ marginTop: 8, fontFamily: "Inter, sans-serif", fontSize: 12.5, lineHeight: 1.45, color: "rgba(0,0,0,.6)", maxWidth: 220 }}>({s.sub})</div>
              )}
            </div>
          ))}
        </div>
        <div style={{ marginTop: 56 }}>
          <a href="#" className="btn btn-lg" onClick={(e) => { e.preventDefault(); go("results"); }} style={{ background: "var(--ink)", color: "var(--bg)" }}>
            {t("home.numbers.cta")} <Icon name="arrow-right" size={16} />
          </a>
        </div>
      </div>
      <style>{`
        @media (max-width: 900px) {
          .big-num-grid { grid-template-columns: 1fr !important; }
          .big-num-cell { border-right: 0 !important; border-bottom: 1px solid rgba(0,0,0,.18); }
          .big-num-cell:last-child { border-bottom: 0 !important; }
        }
      `}</style>
    </section>
  );
}

const FEATURE_IMAGES = [
  "/assets/tabletalk_benefits_home_instant_feedback3-1200.webp",
  "/assets/tabletalk_benefits_home_instant_alerts-1200.webp",
  "/assets/tabletalk_benefits_home_guest_recovery-1200.webp",
  "/assets/tabletalk_benefits_home_online_reputation-1200.webp",
  "/assets/tabletalk_benefits_home_team_performance-1200.webp",
  "/assets/tabletalk_benefits_home_customer_database-1200.webp"
];

function BoldFeatures() {
  const { t } = useTranslation();
  const items = t("home.features.items", { returnObjects: true });
  const FEATURES = items.map((it, i) => ({ key: String(i), tab: it.tab, tagline: it.tagline, body: it.body, img: FEATURE_IMAGES[i] }));
  const [active, setActive] = useState(0);
  const cur = FEATURES[active];
  const total = String(FEATURES.length).padStart(2, "0");

  return (
    <section style={{ background: "var(--bg)", borderBottom: "1px solid var(--line)", padding: "140px 0" }}>
      <div className="container">
        <div className="feat-head">
          <Eyebrow>{t("home.features.eyebrow")}</Eyebrow>
          <h2 style={{ margin: "24px 0 0", maxWidth: 1100, fontFamily: "Satoshi, Inter, sans-serif", fontWeight: 700, fontSize: "clamp(48px, 6vw, 96px)", lineHeight: 0.95, letterSpacing: "-0.04em", color: "var(--ink)" }}>
            {t("home.features.headline")}{" "}
            <span style={{ fontFamily: "Fraunces, serif", fontStyle: "italic", fontWeight: 400 }}>{t("home.features.headlineItalic")}</span>
          </h2>
          <p style={{ margin: "36px 0 0", maxWidth: 720, fontSize: 18, lineHeight: 1.6, color: "var(--ink-2)" }}>
            {t("home.features.intro")}
          </p>
        </div>

        <div style={{ marginTop: 88, display: "grid", gridTemplateColumns: "minmax(0, 1fr) minmax(0, 1.05fr)", gap: 64, alignItems: "flex-start" }} className="feat-body">
          <div className="feat-image">
            <img
              key={cur.key}
              src={cur.img}
              alt={cur.tab}
              loading="lazy"
              decoding="async"
            />
            <div className="feat-image-shade" aria-hidden="true" />
            <div className="feat-image-tag" aria-hidden="true">
              <span className="feat-image-num">{String(active + 1).padStart(2, "0")}</span>
              <span className="feat-image-sep">/</span>
              <span className="feat-image-total">{total}</span>
              <span className="feat-image-bar" />
              <span className="feat-image-label">{cur.tab}</span>
            </div>
          </div>

          <div className="feat-list" role="tablist" aria-label={t("home.features.ariaList")}>
            {FEATURES.map((f, i) => {
              const isActive = i === active;
              return (
                <button
                  key={f.key}
                  type="button"
                  role="tab"
                  aria-selected={isActive}
                  onClick={() => setActive(i)}
                  className="feat-row"
                  data-active={isActive}
                >
                  <div className="feat-row-head">
                    <span className="feat-num">{String(i + 1).padStart(2, "0")}</span>
                    <span className="feat-name">{f.tab}</span>
                    <span className="feat-mark" aria-hidden="true" />
                  </div>
                  <div className="feat-row-body">
                    <div className="feat-row-body-inner">
                      <div className="feat-row-tagline">{f.tagline}</div>
                      <p className="feat-row-text">{f.body}</p>
                    </div>
                  </div>
                </button>
              );
            })}
          </div>
        </div>
      </div>
      <style>{`
        .feat-image {
          position: relative;
          width: 100%;
          border-radius: 14px;
          overflow: hidden;
          aspect-ratio: 4 / 5;
          background: var(--ink);
          box-shadow: 0 24px 60px -34px rgba(16,14,12,0.32), 0 6px 14px -10px rgba(16,14,12,0.12);
        }
        .feat-image img {
          width: 100%;
          height: 100%;
          object-fit: cover;
          display: block;
          animation: tt-feat-fade .4s ease;
        }
        .feat-image-shade {
          position: absolute; inset: 0;
          background: linear-gradient(180deg, transparent 55%, rgba(0,0,0,.55) 100%);
          pointer-events: none;
          z-index: 1;
        }
        .feat-image-tag {
          position: absolute; left: 22px; bottom: 22px; right: 22px;
          display: flex; align-items: center; gap: 10px;
          color: var(--bg);
          font-family: 'Inter', sans-serif;
          font-size: 12px; font-weight: 600;
          letter-spacing: .14em; text-transform: uppercase;
          z-index: 2;
        }
        .feat-image-num { color: var(--persimmon); }
        .feat-image-sep, .feat-image-total { opacity: .55; }
        .feat-image-bar {
          flex-shrink: 0; width: 22px; height: 1px;
          background: var(--persimmon); margin: 0 4px;
        }
        .feat-image-label { letter-spacing: .12em; }

        .feat-list { display: flex; flex-direction: column; }
        .feat-row {
          all: unset;
          cursor: pointer;
          display: block;
          padding: 24px 0 22px;
          border-top: 1px solid var(--line);
          position: relative;
          transition: padding .25s ease;
        }
        .feat-row:last-child { border-bottom: 1px solid var(--line); }
        .feat-row:focus-visible {
          outline: none;
        }
        .feat-row:focus-visible .feat-name { color: var(--persimmon); }
        .feat-row::before {
          content: "";
          position: absolute;
          left: -20px;
          top: 32px;
          width: 3px;
          height: 0;
          background: var(--persimmon);
          transition: height .35s ease;
        }
        .feat-row[data-active="true"]::before { height: 26px; }
        .feat-row-head {
          display: grid;
          grid-template-columns: 56px 1fr 18px;
          align-items: center;
          gap: 18px;
        }
        .feat-num {
          font-family: 'Satoshi', 'Inter', sans-serif;
          font-weight: 700;
          font-size: 13px;
          letter-spacing: .18em;
          color: var(--persimmon);
        }
        .feat-name {
          font-family: 'Satoshi', 'Inter', sans-serif;
          font-weight: 600;
          font-size: 21px;
          letter-spacing: -0.012em;
          color: var(--muted);
          transition: color .2s ease;
        }
        .feat-row:hover .feat-name { color: var(--ink); }
        .feat-row[data-active="true"] .feat-name {
          color: var(--ink);
          font-size: 26px;
        }
        .feat-mark {
          width: 12px; height: 12px;
          border-radius: 50%;
          border: 1px solid var(--line-2);
          background: transparent;
          transition: background .2s ease, border-color .2s ease, transform .2s ease;
          justify-self: end;
        }
        .feat-row[data-active="true"] .feat-mark {
          background: var(--persimmon);
          border-color: var(--persimmon);
          transform: scale(1.1);
        }
        .feat-row-body {
          display: grid;
          grid-template-rows: 0fr;
          transition: grid-template-rows .35s ease;
        }
        .feat-row[data-active="true"] .feat-row-body { grid-template-rows: 1fr; }
        .feat-row-body-inner {
          overflow: hidden;
          padding-left: 74px;
        }
        .feat-row[data-active="true"] .feat-row-body-inner { padding-top: 18px; }
        .feat-row-tagline {
          font-family: 'Satoshi', 'Inter', sans-serif;
          font-weight: 600;
          font-size: 15px;
          color: var(--persimmon);
        }
        .feat-row-text {
          margin: 10px 0 4px;
          font-size: 16px;
          line-height: 1.65;
          color: var(--ink-2);
          max-width: 560px;
        }
        @keyframes tt-feat-fade {
          from { opacity: 0; transform: scale(1.015); }
          to   { opacity: 1; transform: scale(1); }
        }
        @media (prefers-reduced-motion: reduce) {
          .feat-image img { animation: none; }
          .feat-row::before, .feat-name, .feat-mark, .feat-row-body { transition: none; }
        }
        @media (max-width: 900px) {
          .feat-body { grid-template-columns: 1fr !important; gap: 40px !important; }
          .feat-image { aspect-ratio: 3 / 4; }
          .feat-row[data-active="true"] .feat-name { font-size: 22px; }
          .feat-row-head { grid-template-columns: 44px 1fr 14px !important; gap: 14px !important; }
          .feat-row-body-inner { padding-left: 58px !important; }
        }
      `}</style>
    </section>
  );
}

function BoldFounder() {
  const { t } = useTranslation();
  return (
    <section style={{ background: "var(--bg)", padding: "140px 0", borderTop: "1px solid var(--line)", borderBottom: "1px solid var(--line)" }}>
      <div className="container">
        <Eyebrow>{t("home.founder.eyebrow")}</Eyebrow>
        <div className="founder-card">
          <div className="founder-quote tt-edit">
            <p>{t("home.founder.p1")}</p>
            <p>{t("home.founder.p2")}</p>
            <p>{t("home.founder.p3")}</p>
            <p className="founder-closer">{t("home.founder.closer")}</p>
          </div>
          <div className="founder-sig">
            <img src="/assets/signature.webp" alt={t("home.founder.signatureAlt")} loading="lazy" decoding="async" />
          </div>
        </div>
      </div>
      <style>{`
        .founder-card {
          margin-top: 48px;
          background: var(--bg-2);
          border-left: 12px solid var(--marigold);
          border-radius: 4px;
          padding: 72px 80px;
        }
        .founder-quote {
          font-size: clamp(22px, 2.3vw, 32px);
          line-height: 1.4;
          color: var(--ink);
          max-width: 940px;
        }
        .founder-quote p {
          margin: 0 0 22px;
        }
        .founder-quote p:last-child { margin-bottom: 0; }
        .founder-closer { margin-top: 32px !important; }
        .founder-sig {
          margin-top: 48px;
          height: 64px;
          display: flex;
          align-items: center;
        }
        .founder-sig img {
          height: 100%;
          width: auto;
          display: block;
          mix-blend-mode: multiply;
        }
        @media (max-width: 900px) {
          .founder-card { padding: 40px 28px; border-left-width: 8px; }
          .founder-sig { margin-top: 36px; height: 56px; }
        }
      `}</style>
    </section>
  );
}

const SEGMENTS = [
  { id: "indep",  label: "Independent",     tone: "persimmon",
    headline: "For the restaurant you walk every night.",
    body: "One owner, one team, one pulse. The card sits at the host stand or the bill folder. Reviews you used to ask for and never get start arriving the same evening.",
    bullets: ["Setup in an afternoon", "Multilingual by default", "€79 / month, plain"],
    tag: "Most independents start here" },
  { id: "group",  label: "Multi-location",  tone: "coral",
    headline: "For the group with twelve locations and one reputation.",
    body: "Roll-ups across the chain. Section managers see their floor; regional directors see the brand; head office sees the system.",
    bullets: ["Per-location and per-shift reporting", "Cross-location benchmarks", "Branded NFC cards per concept"],
    tag: "House plan · 12+ locations" },
  { id: "hotel",  label: "Hotel F&B",       tone: "ink",
    headline: "For the F&B director with breakfast, in-suite dining, and the bar.",
    body: "In-suite dining cards in the binder. Table cards at breakfast. Bar cards in the lobby. One feedback layer for every cover, tagged by department.",
    bullets: ["Department tags · in-suite dining, breakfast, bar", "PMS-friendly export", "Multilingual for international guests"],
    tag: "Used in 60+ hotel restaurants" }
];

function BoldAudienceSwitcher() {
  const [r, setR] = useState(SEGMENTS[0]);
  const isInk = r.tone === "ink";
  const bg = r.tone === "persimmon" ? "var(--persimmon)" : r.tone === "coral" ? "var(--coral)" : "var(--ink)";
  const fg = isInk ? "var(--bg)" : "var(--ink)";
  const muted = isInk ? "rgba(255,255,255,.72)" : "rgba(0,0,0,.7)";
  const line = isInk ? "rgba(255,255,255,.18)" : "rgba(0,0,0,.18)";

  return (
    <section style={{ background: bg, color: fg, padding: "140px 0", borderBottom: "1px solid var(--line)", transition: "background .4s ease" }}>
      <div className="container">
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-end", flexWrap: "wrap", gap: 24 }}>
          <div>
            <div style={{ fontFamily: "Inter, sans-serif", fontSize: 12.5, letterSpacing: ".18em", textTransform: "uppercase", fontWeight: 600, color: muted }}>Built for the restaurant you run</div>
            <h2 style={{ margin: "24px 0 0", maxWidth: 1000, fontFamily: "Satoshi, Inter, sans-serif", fontWeight: 700, fontSize: "clamp(40px, 5.4vw, 88px)", lineHeight: 0.96, letterSpacing: "-0.04em" }}>{r.headline}</h2>
          </div>
        </div>
        <div style={{ marginTop: 56, display: "flex", gap: 8, flexWrap: "wrap" }}>
          {SEGMENTS.map((x) => (
            <button key={x.id} onClick={() => setR(x)} style={{
              all: "unset", cursor: "pointer", padding: "12px 20px", borderRadius: 99,
              fontFamily: "Satoshi, sans-serif", fontWeight: 600, fontSize: 14,
              border: r.id === x.id ? `1px solid ${fg}` : `1px solid ${line}`,
              background: r.id === x.id ? fg : "transparent",
              color: r.id === x.id ? bg : fg,
              transition: "all .2s ease"
            }}>{x.label}</button>
          ))}
        </div>
        <div style={{ marginTop: 64, display: "grid", gridTemplateColumns: "minmax(0, 1.2fr) minmax(0, 1fr)", gap: 64, alignItems: "flex-start" }} className="aud-bold-grid">
          <div>
            <p style={{ margin: 0, fontSize: "clamp(20px, 1.8vw, 26px)", lineHeight: 1.5, color: fg, maxWidth: 580, fontFamily: "Inter, sans-serif" }}>{r.body}</p>
            <ul style={{ marginTop: 36, padding: 0, listStyle: "none", display: "flex", flexDirection: "column", gap: 14 }}>
              {r.bullets.map((b) => (
                <li key={b} style={{ display: "flex", gap: 12, alignItems: "flex-start", fontSize: 16 }}>
                  <span style={{ flexShrink: 0, marginTop: 6, width: 8, height: 8, borderRadius: "50%", background: fg }} />
                  <span>{b}</span>
                </li>
              ))}
            </ul>
            <div style={{ marginTop: 40, fontSize: 13, letterSpacing: ".04em", color: muted }}>{r.tag}</div>
          </div>
          <PhotoFrame ratio="4/5" tone={isInk ? "wash" : "ink"} caption={r.headline} hint={r.label + " · scenario photo"} />
        </div>
      </div>
      <style>{`@media (max-width: 900px){.aud-bold-grid{grid-template-columns:1fr !important; gap:48px !important}}`}</style>
    </section>
  );
}

function BoldPartnerMarquee() {
  const { t } = useTranslation();
  const names = ["La Ceaun", "Tucano", "Kifla", "Ola Kafe", "Hopaa"];
  const row = [...names, ...names];
  return (
    <section style={{ padding: "80px 0", borderBottom: "1px solid var(--line)", background: "var(--bg)", overflow: "hidden" }}>
      <div className="container">
        <div style={{ fontFamily: "Inter, sans-serif", fontSize: 12.5, letterSpacing: ".18em", textTransform: "uppercase", fontWeight: 600, color: "var(--muted)" }}>{t("home.partners.label")}</div>
      </div>
      <div style={{ marginTop: 32, display: "flex", whiteSpace: "nowrap", animation: "tt-marquee-slow 64s linear infinite" }}>
        {row.map((n, i) => (
          <span key={i} style={{ paddingInline: "40px", fontFamily: "Fraunces, serif", fontStyle: "italic", fontWeight: 400, fontSize: "clamp(48px, 7vw, 112px)", lineHeight: 1, letterSpacing: "-0.02em", color: "var(--ink)", display: "inline-flex", alignItems: "center", gap: 40 }}>
            {n}
            <span style={{ width: 10, height: 10, borderRadius: "50%", background: "var(--persimmon)" }} />
          </span>
        ))}
      </div>
      <style>{`@keyframes tt-marquee-slow { from { transform: translateX(0); } to { transform: translateX(-50%); } }`}</style>
    </section>
  );
}

function BoldCTA() {
  const { t } = useTranslation();
  const go = useLocaleNav();
  return (
    <section style={{ background: "var(--ink)", color: "var(--bg)", padding: "180px 0" }}>
      <div className="container">
        <h2 className="tt-h tt-h-xl" style={{ margin: 0, maxWidth: 1100, color: "var(--bg)" }}>
          {t("home.cta.headline")} <span style={{ fontFamily: "Fraunces, serif", fontStyle: "italic", fontWeight: 400, color: "var(--persimmon)" }}>{t("home.cta.headlineItalic")}</span>
        </h2>
        <p style={{ margin: "40px 0 0", fontFamily: "Inter, sans-serif", fontSize: "clamp(18px, 1.4vw, 22px)", lineHeight: 1.55, color: "rgba(255,255,255,.78)", maxWidth: 720 }}>
          {t("common.freeTrialNote")}
        </p>
        <div style={{ marginTop: 48, display: "flex", gap: 14, flexWrap: "wrap" }}>
          <a href="#" className="btn btn-lg" onClick={(e) => { e.preventDefault(); go("pricing"); }} style={{ background: "var(--persimmon)", color: "var(--ink)" }}>
            {t("common.claimFreeWeek")} <Icon name="arrow-right" size={16} />
          </a>
        </div>
      </div>
    </section>
  );
}
